import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { CashIcon, PencilIcon } from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import ExpandableDescription from "../ExpandableDescription";
import { currencyFormat } from "../../../../Utils";
import { TAX_KIND_LABEL } from "../../../../Constants";

const TYPE_STYLES = {
  SALES: {
    badge: "bg-green-100 text-green-800",
    label: "taxes.types.sales",
  },
  PURCHASES: {
    badge: "bg-orange-100 text-orange-800",
    label: "taxes.types.purchases",
  },
};

const TaxDetailModal = ({ open, onClose, onEdit, tax }) => {
  const { t, i18n } = useTranslation();

  if (!tax) return null;

  const typeStyle = TYPE_STYLES[tax.type] || TYPE_STYLES.SALES;
  const isPercentage = tax.computation === "PERCENTAGE";
  const rateDisplay = isPercentage
    ? `${tax.amount}%`
    : currencyFormat(tax.amount);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t("taxes.detail.title")}
      actionButton={
        onEdit && (
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={() => onEdit(tax)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Header Summary */}
        <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                <CashIcon className="h-7 w-7 text-gray-500" />
              </div>
              <div>
                <ExpandableDescription
                  text={tax.name}
                  maxLength={40}
                  textClassName="text-xl font-semibold text-gray-900"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {tax.withholding && (
                <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                  {t("taxes.badges.withheld")}
                </span>
              )}
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${typeStyle.badge}`}
              >
                {t(typeStyle.label)}
              </span>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("taxes.fields.computation")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {isPercentage
                  ? t("taxes.computations.percentage")
                  : t("taxes.computations.fixed")}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("taxes.fields.rate")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {rateDisplay}
              </dd>
            </div>

            {tax.kind && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("taxes.fields.kind")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {t(TAX_KIND_LABEL[tax.kind] || "taxes.kinds.other")}
                </dd>
              </div>
            )}

            {tax.createdAt && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  {t("taxes.fields.date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dayjs(tax.createdAt)
                    .locale(i18n.language)
                    .format("DD MMMM YYYY")}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Tax Account Section */}
        {tax.account && (
          <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
            <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                {t("taxes.fields.taxAccount")}
              </h4>
            </div>
            <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("taxes.fields.account")}
                </dt>
                <dd className="mt-1 text-sm font-semibold text-gray-900">
                  {tax.account.code}
                </dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("common.name")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <ExpandableDescription
                    text={tax.account.name}
                    maxLength={40}
                  />
                </dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </SlideOver>
  );
};

export default TaxDetailModal;
